import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AdminPhysicians from "../components/AdminPhysicians";
import {
  DashboardPatientDetail,
  DashboardStats,
  ScoreSummary,
  getDashboardStats,
} from "../api/stats";

const DEFAULT_FROM = "2026-04-14";

const SEX_LABELS: Record<string, string> = {
  male: "M",
  female: "F",
  other: "O",
};

// Patient ID format: {age}{sex}{seq} (legacy: KZ2-{age}{sex}{seq}), e.g. 55M435 → seq = 435
function patientSeq(patientId?: string | null): string {
  if (!patientId) return "";
  const m = patientId.match(/[MFO](\d+)$/i);
  return m ? m[1] : "";
}

// Pure-digit query → match the trailing sequential number; otherwise substring over the full ID.
function matchesPatientSearch(patientId: string | null | undefined, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  if (!patientId) return false;
  if (/^\d+$/.test(q)) {
    const seq = patientSeq(patientId);
    return seq === q || seq.startsWith(q);
  }
  return patientId.toLowerCase().includes(q);
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  excellent: { label: "Excellent", color: "#2e7d32" },
  good: { label: "Good", color: "#66bb6a" },
  fair: { label: "Fair", color: "#f9a825" },
  poor: { label: "Poor", color: "#ef6c00" },
  failed: { label: "Failed", color: "#c62828" },
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Almaty",
  });
}

function StatCard({
  label,
  value,
  sublabel,
  color,
}: {
  label: string;
  value: string | number;
  sublabel?: string;
  color?: string;
}) {
  return (
    <Card>
      <CardContent sx={{ textAlign: "center", py: 1.5 }}>
        <Typography variant="h4" sx={{ color: color || "inherit", fontWeight: 600 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          {label}
        </Typography>
        {sublabel && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontSize: 11 }}>
            {sublabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BarChart({
  data,
  height = 180,
  color = "#1976d2",
  labelFormatter,
}: {
  data: { label: string; count: number }[];
  height?: number;
  color?: string;
  labelFormatter?: (label: string) => string;
}) {
  if (data.length === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          No data
        </Typography>
      </Box>
    );
  }

  const rawMax = Math.max(...data.map((d) => d.count));
  const niceMax = rawMax === 0 ? 4 : Math.max(4, Math.ceil(rawMax / 4) * 4);
  const yTicks = 4;
  const tickValues = Array.from({ length: yTicks + 1 }, (_, i) => Math.round((niceMax / yTicks) * i));

  const paddingLeft = 36;
  const paddingRight = 12;
  const paddingTop = 14;
  const paddingBottom = 28;
  const width = 600;
  const innerW = width - paddingLeft - paddingRight;
  const innerH = height - paddingTop - paddingBottom;
  const barGap = 0.35;
  const slot = innerW / data.length;
  const barW = slot * (1 - barGap);

  return (
    <Box sx={{ width: "100%" }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        <defs>
          <linearGradient id="barGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="1" />
            <stop offset="100%" stopColor={color} stopOpacity="0.55" />
          </linearGradient>
        </defs>
        {tickValues.map((t, i) => {
          const y = paddingTop + innerH - (t / niceMax) * innerH;
          return (
            <g key={i}>
              <line
                x1={paddingLeft}
                x2={width - paddingRight}
                y1={y}
                y2={y}
                stroke="#e0e0e0"
                strokeDasharray={i === 0 ? "0" : "3 3"}
                strokeWidth={1}
              />
              <text x={paddingLeft - 6} y={y + 4} fontSize="10" fill="#9e9e9e" textAnchor="end">
                {t}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = d.count === 0 ? 0 : (d.count / niceMax) * innerH;
          const x = paddingLeft + slot * i + (slot - barW) / 2;
          const y = paddingTop + innerH - h;
          const display = labelFormatter ? labelFormatter(d.label) : d.label;
          const labelX = paddingLeft + slot * i + slot / 2;
          return (
            <g key={i}>
              {d.count > 0 && (
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={h}
                  fill="url(#barGradient)"
                  rx={3}
                  ry={3}
                >
                  <title>
                    {display}: {d.count}
                  </title>
                </rect>
              )}
              {d.count > 0 && (
                <text
                  x={labelX}
                  y={y - 4}
                  fontSize="11"
                  fontWeight="600"
                  fill="#424242"
                  textAnchor="middle"
                >
                  {d.count}
                </text>
              )}
              <text
                x={labelX}
                y={height - 10}
                fontSize="10"
                fill="#757575"
                textAnchor="middle"
              >
                {display}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

function PatientsByDayChart({ data, height = 280 }: { data: { date: string; count: number }[]; height?: number }) {
  const options = useMemo<Highcharts.Options>(() => {
    const categories = data.map((d) => d.date);
    const counts = data.map((d) => d.count);
    const minPxPerBar = 32;
    const visibleBars = Math.max(1, Math.floor(900 / minPxPerBar));
    const needsScroll = data.length > visibleBars;

    return {
      chart: {
        type: "column",
        height,
        spacingTop: 12,
        spacingBottom: 8,
        style: { fontFamily: "inherit" },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: {
        categories,
        crosshair: true,
        min: needsScroll ? Math.max(0, data.length - visibleBars) : undefined,
        max: needsScroll ? data.length - 1 : undefined,
        scrollbar: {
          enabled: needsScroll,
          height: 12,
          barBackgroundColor: "#bdbdbd",
          barBorderRadius: 6,
          barBorderWidth: 0,
          buttonBackgroundColor: "#e0e0e0",
          buttonBorderWidth: 0,
          buttonBorderRadius: 6,
          trackBackgroundColor: "#f5f5f5",
          trackBorderWidth: 0,
          trackBorderRadius: 6,
        },
        labels: {
          rotation: -45,
          style: { fontSize: "11px", color: "#757575" },
          formatter: function () {
            const iso = String(this.value);
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return iso;
            return d.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              timeZone: "Asia/Almaty",
            });
          },
        },
      },
      yAxis: {
        title: { text: undefined },
        allowDecimals: false,
        gridLineColor: "#e0e0e0",
        gridLineDashStyle: "Dash",
        min: 0,
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const ctx = this as any;
          const iso = String(ctx.point?.category ?? ctx.key ?? "");
          const d = new Date(iso);
          const label = Number.isNaN(d.getTime())
            ? iso
            : d.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
                timeZone: "Asia/Almaty",
              });
          return `<b>${label}</b><br/>Patients: <b>${this.y}</b>`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 3,
          pointPadding: 0.05,
          groupPadding: 0.1,
          color: {
            linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
            stops: [
              [0, "#1976d2"],
              [1, "rgba(25, 118, 210, 0.55)"],
            ],
          },
          dataLabels: {
            enabled: true,
            style: { fontSize: "10px", fontWeight: "600", textOutline: "none", color: "#424242" },
            formatter: function () {
              return this.y && this.y > 0 ? String(this.y) : "";
            },
          },
        },
      },
      series: [
        {
          type: "column",
          name: "Patients",
          data: counts,
        },
      ],
      scrollbar: { enabled: needsScroll, liveRedraw: true },
      navigator: { enabled: false },
    };
  }, [data, height]);

  if (data.length === 0) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          No data
        </Typography>
      </Box>
    );
  }

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function SexBalanceBar({ male, female, other }: { male: number; female: number; other: number }) {
  const total = male + female + other;
  if (total === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        No data
      </Typography>
    );
  }
  const mp = (male / total) * 100;
  const fp = (female / total) * 100;
  const op = (other / total) * 100;
  return (
    <Box>
      <Box sx={{ display: "flex", height: 28, borderRadius: 1, overflow: "hidden", border: "1px solid #e0e0e0" }}>
        <Tooltip title={`Male: ${male} (${mp.toFixed(0)}%)`} arrow>
          <Box sx={{ width: `${mp}%`, bgcolor: "#1976d2", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 600 }}>
            {mp > 10 ? `M ${male}` : ""}
          </Box>
        </Tooltip>
        <Tooltip title={`Female: ${female} (${fp.toFixed(0)}%)`} arrow>
          <Box sx={{ width: `${fp}%`, bgcolor: "#e91e63", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 600 }}>
            {fp > 10 ? `F ${female}` : ""}
          </Box>
        </Tooltip>
        {op > 0 && (
          <Tooltip title={`Other: ${other} (${op.toFixed(0)}%)`} arrow>
            <Box sx={{ width: `${op}%`, bgcolor: "#9e9e9e" }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: "#1976d2", fontWeight: 600 }}>
          M {male} ({mp.toFixed(0)}%)
        </Typography>
        <Typography variant="caption" sx={{ color: "#e91e63", fontWeight: 600 }}>
          F {female} ({fp.toFixed(0)}%)
        </Typography>
      </Box>
    </Box>
  );
}

function ScoreCard({ title, data }: { title: string; data: ScoreSummary }) {
  return (
    <Card sx={{ flex: 1, minWidth: 220 }}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        {data.count === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No scores recorded
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Avg
                </Typography>
                <Typography variant="h6">{data.avg}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Min
                </Typography>
                <Typography variant="h6">{data.min}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Max
                </Typography>
                <Typography variant="h6">{data.max}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Filled
                </Typography>
                <Typography variant="h6">{data.count}</Typography>
              </Box>
            </Stack>
            {data.buckets.length > 0 && (
              <BarChart
                data={data.buckets.map((b) => ({ label: b.label, count: b.count }))}
                height={80}
                color="#42a5f5"
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CalibrationStatusRow({ status_counts }: { status_counts: Record<string, number> }) {
  const entries = Object.entries(status_counts);
  if (entries.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        No attempts in this period
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }} useFlexGap>
      {entries.map(([s, n]) => {
        const info = STATUS_LABELS[s] || { label: s, color: "#757575" };
        return (
          <Chip
            key={s}
            label={`${info.label}: ${n}`}
            size="small"
            sx={{ bgcolor: info.color, color: "white", fontWeight: 600 }}
          />
        );
      })}
    </Stack>
  );
}

const ALL_CATEGORIES = ["ipad", "monitor", "unknown"];
const ALL_PARTS: number[] = [1, 2, 3];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(todayIso());
  const [selectedDevices, setSelectedDevices] = useState<string[]>(ALL_CATEGORIES);
  const [includeNoCalibration, setIncludeNoCalibration] = useState<boolean>(true);
  const [selectedParts, setSelectedParts] = useState<number[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientSearch, setPatientSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const devices = selectedDevices.length > 0 ? selectedDevices.join(",") : undefined;
      const data = await getDashboardStats({
        from,
        to,
        devices,
        include_no_calibration: includeNoCalibration,
        passed_parts: selectedParts.join(","),
      });
      setStats(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [from, to, selectedDevices, includeNoCalibration, selectedParts]);

  const toggleDevice = (id: string) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]
    );
  };

  const togglePart = (n: number) => {
    setSelectedParts((prev) =>
      prev.includes(n) ? prev.filter((p) => p !== n) : [...prev, n].sort()
    );
  };

  useEffect(() => {
    load();
  }, [load]);

  const attemptsRows = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.calibration.attempts_to_pass_distribution)
      .map(([k, v]) => ({ attempt: Number(k), count: v }))
      .sort((a, b) => a.attempt - b.attempt);
  }, [stats]);

  const filteredDetails = useMemo(() => {
    if (!stats) return [];
    const q = patientSearch.trim();
    if (!q) return stats.details;
    return stats.details.filter((d) => matchesPatientSearch(d.patient_id, q));
  }, [stats, patientSearch]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5">Admin Dashboard</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate("/doctor/create-patient")}>
          New Patient
        </Button>
      </Box>

      {/* Production-режим: врачи (physician) + их команды медсестёр */}
      <AdminPhysicians />

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap", mb: 1 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button variant="outlined" onClick={load} disabled={loading}>
            Refresh
          </Button>
          {stats && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Period: {stats.range.from} → {stats.range.to}
            </Typography>
          )}
        </Stack>
        {stats && stats.available_devices.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>
              Device:
            </Typography>
            {stats.available_devices.map((d) => (
              <FormControlLabel
                key={d.id}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedDevices.includes(d.id)}
                    onChange={() => toggleDevice(d.id)}
                  />
                }
                label={`${d.label} (${d.count})`}
              />
            ))}
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeNoCalibration}
                onChange={(e) => setIncludeNoCalibration(e.target.checked)}
              />
            }
            label="Show all (including without calibration)"
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>
            Passed:
          </Typography>
          {ALL_PARTS.map((n) => (
            <FormControlLabel
              key={n}
              control={
                <Checkbox
                  size="small"
                  checked={selectedParts.includes(n)}
                  onChange={() => togglePart(n)}
                />
              }
              label={`Part ${n}`}
            />
          ))}
        </Box>
      </Paper>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: "#fff3e0" }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading && !stats ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : stats ? (
        <Box sx={{ position: "relative" }}>
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.55)",
                zIndex: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                pt: 10,
                pointerEvents: "none",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, 1fr)",
              },
              gap: 2,
              mb: 3,
            }}
          >
            <StatCard label="Patients in period" value={stats.patients.total} />
            <StatCard
              label="Calibration passed"
              value={stats.calibration.passed_patients}
              sublabel={`${stats.calibration.pass_rate}% of attempted`}
              color="#2e7d32"
            />
            <StatCard
              label="Calibration failed"
              value={stats.calibration.failed_patients}
              sublabel={`${stats.calibration.fail_rate}% of attempted`}
              color="#c62828"
            />
            <StatCard
              label="No attempts"
              value={stats.calibration.no_calibration_patients}
              sublabel="did not reach calibration"
              color="#757575"
            />
            <StatCard label="Total attempts" value={stats.calibration.total_attempts} />
            <StatCard
              label="Average error"
              value={stats.calibration.avg_error_deg_passed != null ? `${stats.calibration.avg_error_deg_passed}°` : "—"}
              sublabel="across passed attempts"
              color="#1565c0"
            />
            <StatCard label="Male" value={stats.patients.male} color="#1976d2" />
            <StatCard label="Female" value={stats.patients.female} color="#e91e63" />
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Patients by day
                </Typography>
                <PatientsByDayChart data={stats.patients.by_day} height={280} />
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Sex balance
                </Typography>
                <SexBalanceBar
                  male={stats.patients.male}
                  female={stats.patients.female}
                  other={stats.patients.other}
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Attempt statuses (all attempts)
                </Typography>
                <CalibrationStatusRow status_counts={stats.calibration.status_counts} />
                {attemptsRows.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Pass attempt distribution
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {attemptsRows.map((r) => (
                        <Chip
                          key={r.attempt}
                          label={`Attempt ${r.attempt}: ${r.count}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </>
                )}
              </CardContent>
            </Card>
          </Stack>

          {stats.by_device.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: `repeat(${Math.min(stats.by_device.length, 3)}, 1fr)` },
                gap: 2,
                mb: 3,
              }}
            >
              {stats.by_device.map((d) => (
                <Card key={d.id}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {d.label}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Patients
                        </Typography>
                        <Typography variant="h6">{d.patients}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Passed
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#2e7d32" }}>
                          {d.passed} ({d.pass_rate}%)
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Error
                        </Typography>
                        <Typography variant="h6">
                          {d.avg_error_deg != null ? `${d.avg_error_deg}°` : "—"}
                        </Typography>
                      </Box>
                    </Stack>
                    {d.models.length > 0 && (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }} useFlexGap>
                        {d.models.map((m) => (
                          <Chip key={m.label} label={`${m.label}: ${m.count}`} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
            <ScoreCard title="MoCA (0–30)" data={stats.scores.moca} />
            <ScoreCard title="Qmci (0–100)" data={stats.scores.qmci} />
            <ScoreCard title="Depression" data={stats.scores.depression} />
          </Stack>

          <Paper sx={{ mb: 3 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Patient details ({filteredDetails.length}
                {patientSearch.trim() ? ` / ${stats.details.length}` : ""})
              </Typography>
              <TextField
                size="small"
                placeholder="Поиск по номеру (напр. 435)"
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
                sx={{ minWidth: 240 }}
                slotProps={{
                  input: {
                    endAdornment: patientSearch ? (
                      <Button size="small" onClick={() => setPatientSearch("")} sx={{ minWidth: 0, px: 1 }}>
                        ✕
                      </Button>
                    ) : null,
                  },
                }}
              />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#fafafa" }}>
                    <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sex</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Age</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Calibration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Pass attempt</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Error, °</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>All attempts</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Parts</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>MoCA</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Qmci</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Depression</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDetails.map((d: DashboardPatientDetail) => {
                    const statusInfo = d.calibration_status ? STATUS_LABELS[d.calibration_status] : null;
                    return (
                      <TableRow
                        key={d.patient_id || Math.random()}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => d.patient_id && navigate(`/patient/${d.patient_id}`)}
                      >
                        <TableCell>
                          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>
                            {d.patient_id || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{d.sex ? SEX_LABELS[d.sex] || d.sex : "—"}</TableCell>
                        <TableCell>{d.age ?? "—"}</TableCell>
                        <TableCell>{d.created_at ? formatDateTime(d.created_at) : "—"}</TableCell>
                        <TableCell>
                          {d.device_label ? (
                            <Chip label={d.device_label} size="small" variant="outlined" />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {d.calibration_attempts_total === 0 ? (
                            <Chip
                              icon={<HelpOutlineIcon />}
                              label="None"
                              size="small"
                              sx={{ bgcolor: "#eeeeee" }}
                            />
                          ) : d.calibration_passed ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={statusInfo?.label || d.calibration_status || "—"}
                              size="small"
                              sx={{ bgcolor: statusInfo?.color || "#2e7d32", color: "white", fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              icon={<CancelIcon />}
                              label={statusInfo?.label || d.calibration_status || "Failed"}
                              size="small"
                              sx={{ bgcolor: statusInfo?.color || "#c62828", color: "white", fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{d.calibration_pass_attempt ?? "—"}</TableCell>
                        <TableCell>
                          {d.calibration_mean_error_deg != null
                            ? d.calibration_mean_error_deg.toFixed(2)
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {(d.calibration_statuses?.length ?? 0) > 0 ? (
                            <Stack direction="row" spacing={0.5}>
                              {(d.calibration_statuses ?? []).map((s, i) => {
                                const info = STATUS_LABELS[s] || { label: s, color: "#757575" };
                                return (
                                  <Tooltip key={i} title={`Attempt ${i + 1}: ${info.label}`} arrow>
                                    <Box
                                      sx={{
                                        width: 10,
                                        height: 18,
                                        bgcolor: info.color,
                                        borderRadius: 0.5,
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                              <Typography variant="caption" sx={{ ml: 0.5 }}>
                                {d.calibration_attempts_total}
                              </Typography>
                            </Stack>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {d.passed_parts && d.passed_parts.length > 0 ? (
                            <Stack direction="row" spacing={0.5}>
                              {[1, 2, 3].map((n) => (
                                <Chip
                                  key={n}
                                  label={n}
                                  size="small"
                                  sx={{
                                    minWidth: 24,
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    bgcolor: d.passed_parts.includes(n) ? "#2e7d32" : "#e0e0e0",
                                    color: d.passed_parts.includes(n) ? "white" : "#9e9e9e",
                                  }}
                                />
                              ))}
                            </Stack>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>{d.moca_score ?? "—"}</TableCell>
                        <TableCell>{d.qmci_score ?? "—"}</TableCell>
                        <TableCell>{d.depression_score ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredDetails.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        <Typography sx={{ color: "text.secondary", py: 4 }}>
                          {patientSearch.trim()
                            ? `Пациент с номером «${patientSearch.trim()}» не найден в выбранном периоде — расширьте диапазон дат`
                            : "No patients found in this period"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
